import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { FiMail, FiLock, FiEye, FiEyeOff, FiGrid, FiUser, FiDelete, FiLogIn } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, loginWithPin } = useAuth()

  // Detectar si hay empresa guardada para mostrar PIN por defecto
  const savedEmpresaId = localStorage.getItem('pin_empresa_id')
  const savedEmpresaNombre = localStorage.getItem('pin_empresa_nombre')

  // Modo de login: 'email' o 'pin'
  const [loginMode, setLoginMode] = useState(savedEmpresaId ? 'pin' : 'email')

  // Datos formulario email/password
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)

  // Datos PIN (5-8 dígitos)
  const [pin, setPin] = useState('')

  const [loading, setLoading] = useState(false)

  const from = location.state?.from?.pathname || '/'

  // Detectar si es tablet/móvil para mostrar PIN por defecto (si hay empresa guardada)
  useEffect(() => {
    if (savedEmpresaId) {
      const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
      if (isMobileOrTablet) {
        setLoginMode('pin')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Login con email/password
  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await login(formData.email, formData.password)
      toast.success('¡Bienvenido!')
      navigate(from, { replace: true })
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Error al iniciar sesión')
    } finally {
      setLoading(false)
    }
  }

  // Login con PIN
  const handlePinSubmit = async () => {
    if (pin.length < 5) {
      toast.error('El PIN debe tener al menos 5 dígitos')
      return
    }

    if (!savedEmpresaId) {
      toast.error('Inicia sesión primero con email y contraseña')
      setLoginMode('email')
      return
    }

    setLoading(true)

    try {
      await loginWithPin(pin, savedEmpresaId)
      toast.success('¡Bienvenido!')
      navigate(from, { replace: true })
    } catch (error) {
      toast.error(error.response?.data?.error || 'PIN incorrecto')
      setPin('')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  // Manejar teclado numérico para PIN
  const handlePinInput = (digit) => {
    if (pin.length < 8) {
      setPin(pin + digit)
    }
  }

  const handlePinDelete = () => {
    setPin(pin.slice(0, -1))
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg mb-4">
            <FiGrid className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Control Horario
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {loginMode === 'email'
              ? 'Inicia sesión con tu cuenta'
              : 'Introduce tu PIN'
            }
          </p>
        </div>

        {/* Toggle entre modos - solo mostrar si hay empresa guardada */}
        {savedEmpresaId && (
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setLoginMode('email')}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                loginMode === 'email'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <FiUser className="inline mr-2" />
              Email
            </button>
            <button
              type="button"
              onClick={() => setLoginMode('pin')}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                loginMode === 'pin'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <FiLock className="inline mr-2" />
              PIN
            </button>
          </div>
        )}

        {/* Formulario Email/Password */}
        {loginMode === 'email' ? (
          <form onSubmit={handleEmailSubmit} className="card-glass space-y-4">
            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiMail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="input-glass pl-12"
                  placeholder="tu@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input-glass pl-12 pr-12"
                  placeholder="Tu contraseña"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <FiEyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <FiEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-ripple w-full"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>

            <p className="text-sm text-center text-gray-500 mt-4">
              ¿No tienes cuenta?{' '}
              <Link to="/registro" className="text-blue-600 hover:text-blue-800 font-medium">
                Registra tu empresa
              </Link>
            </p>
          </form>
        ) : (
          /* Formulario PIN con teclado táctil */
          <div className="card-glass space-y-6">
            {/* Indicador de empresa */}
            {savedEmpresaNombre && (
              <div className="text-center">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {savedEmpresaNombre}
                </span>
              </div>
            )}

            {/* Display del PIN */}
            <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl p-6">
              <div className="flex justify-center gap-2">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
                  <div
                    key={index}
                    className={`w-10 h-12 sm:w-12 sm:h-14 rounded-xl flex items-center justify-center text-2xl font-bold transition-all ${
                      index < 5
                        ? pin.length > index
                          ? 'bg-blue-600 text-white shadow-lg scale-110'
                          : 'bg-white text-gray-300 border-2 border-gray-200'
                        : pin.length > index
                          ? 'bg-blue-600 text-white shadow-lg scale-110'
                          : index < pin.length + 3 && pin.length >= 5
                            ? 'hidden'
                            : 'bg-white/50 text-gray-200 border-2 border-gray-100'
                    } ${index >= 5 && pin.length < 5 ? 'opacity-30' : ''}`}
                  >
                    {pin.length > index ? '●' : '-'}
                  </div>
                ))}
              </div>
              {pin.length > 0 && pin.length < 5 && (
                <p className="text-center text-xs text-blue-600 mt-2">
                  {5 - pin.length} dígito{5 - pin.length !== 1 ? 's' : ''} más
                </p>
              )}
            </div>

            {/* Teclado numérico táctil */}
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                <button
                  key={digit}
                  type="button"
                  onClick={() => handlePinInput(digit.toString())}
                  disabled={loading || pin.length >= 8}
                  className="btn bg-white hover:bg-gray-50 text-gray-900 text-2xl font-semibold h-16 border border-gray-200 shadow-sm active:scale-95 disabled:opacity-50"
                >
                  {digit}
                </button>
              ))}

              {/* Fila inferior: Borrar, 0, Entrar */}
              <button
                type="button"
                onClick={handlePinDelete}
                disabled={loading || pin.length === 0}
                className="btn bg-red-50 hover:bg-red-100 text-red-600 h-16 border border-red-200 active:scale-95 disabled:opacity-50"
              >
                <FiDelete className="h-6 w-6 mx-auto" />
              </button>

              <button
                type="button"
                onClick={() => handlePinInput('0')}
                disabled={loading || pin.length >= 8}
                className="btn bg-white hover:bg-gray-50 text-gray-900 text-2xl font-semibold h-16 border border-gray-200 shadow-sm active:scale-95 disabled:opacity-50"
              >
                0
              </button>

              <button
                type="button"
                onClick={handlePinSubmit}
                disabled={loading || pin.length < 5}
                className="btn bg-green-500 hover:bg-green-600 text-white text-sm font-bold h-16 border border-green-600 active:scale-95 disabled:opacity-30 disabled:bg-gray-200 disabled:text-gray-400 disabled:border-gray-200 transition-all"
              >
                {loading ? (
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <FiLogIn className="h-6 w-6 mx-auto" />
                )}
              </button>
            </div>

            {/* Enlace para usar email */}
            <button
              type="button"
              onClick={() => setLoginMode('email')}
              className="w-full text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
            >
              Usar email y contraseña
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default Login
